import { toast } from "sonner";
import {
  useCreateToeCheck,
  useSignOffToeCheck,
  useUnlockToeCheck,
  useDeleteToeCheck,
} from "@/api/hooks";
import type { BuildId, ToeCheckId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type ToeCheck = components["schemas"]["ToeCheckResponse"];

export interface ToeCheckActions {
  handleCreate: () => void;
  handleSignOff: (id: ToeCheckId) => void;
  handleUnlock: (id: ToeCheckId) => void;
  handleDelete: (id: ToeCheckId) => void;
  isCreating: boolean;
  isSigningOff: boolean;
  isUnlocking: boolean;
  isDeleting: boolean;
  toeChecks: ToeCheck[];
  signedOffCount: number;
}

export function useToeCheckActions(
  buildId: BuildId,
  toeChecks: ToeCheck[] | undefined,
): ToeCheckActions {
  const create = useCreateToeCheck(buildId);
  const signOff = useSignOffToeCheck(buildId);
  const unlock = useUnlockToeCheck(buildId);
  const del = useDeleteToeCheck(buildId);

  const checks = toeChecks ?? [];
  const signedOffCount = checks.filter((tc) => tc.signed_off_at).length;

  const handleCreate = () => {
    create.mutate(undefined, {
      onSuccess: () => toast.success("Toe check recorded"),
      onError: (err) => toast.error(err.message),
    });
  };

  const handleSignOff = (id: ToeCheckId) => {
    signOff.mutate(id, {
      onSuccess: () => toast.success("Toe check signed off"),
      onError: (err) => toast.error(err.message),
    });
  };

  const handleUnlock = (id: ToeCheckId) => {
    unlock.mutate(id, {
      onSuccess: () => toast.success("Toe check unlocked"),
      onError: (err) => toast.error(err.message),
    });
  };

  const handleDelete = (id: ToeCheckId) => {
    del.mutate(id, {
      onSuccess: () => toast.success("Toe check deleted"),
      onError: (err) => toast.error(err.message),
    });
  };

  return {
    handleCreate,
    handleSignOff,
    handleUnlock,
    handleDelete,
    isCreating: create.isPending,
    isSigningOff: signOff.isPending,
    isUnlocking: unlock.isPending,
    isDeleting: del.isPending,
    toeChecks: checks,
    signedOffCount,
  };
}
